// Offline first outbox.
//
// Everything the app writes goes in here first and is drained by
// src/lib/sync.ts when the network comes back, in insertion order, with
// retry and exponential backoff. This is the reliability guarantee in
// docs/CONTRACT.md: the store is KEYED BY `clientId`, so enqueueing the
// same logical write twice (a double tap, a resumed session, a retry that
// actually succeeded server side) overwrites one record instead of creating
// a second one. A duplicate moment is impossible by construction on the
// client, and the server's UNIQUE (trip_id, client_id) closes the same hole
// on its side.
//
// IndexedDB is the real store. A WebView with storage disabled, private
// mode, or a test runner without IndexedDB falls back to an in memory store
// with identical semantics so the app degrades instead of throwing.
import type { MomentMediaInput, Privacy, TrackPointInput } from "./types";

const DB_NAME = "traveldiary";
const DB_VERSION = 1;
const STORE = "outbox";

export interface PendingMedia {
  /** Local blob, dropped once the server hands back a storage key. */
  blob: Blob | null;
  mime: string;
  width: number | null;
  height: number | null;
  /** Set after a successful POST /media, so a retry never re-uploads. */
  storageKey: string | null;
}

export interface PendingMoment {
  lat: number | null;
  lng: number | null;
  placeName: string | null;
  caption: string;
  capturedAt: string;
  privacy: Privacy;
  media: PendingMedia[];
}

interface OutboxBase {
  clientId: string;
  tripId: string;
  createdAt: string;
  attempts: number;
  /** Epoch millis, the earliest time a drain may retry this item. */
  nextAttemptAt: number;
  lastError: string | null;
}

export interface OutboxMoment extends OutboxBase {
  kind: "moment";
  payload: PendingMoment;
}

export interface OutboxTrack extends OutboxBase {
  kind: "track";
  payload: { points: TrackPointInput[] };
}

export type OutboxItem = OutboxMoment | OutboxTrack;

export function toMomentMediaInputs(media: PendingMedia[]): MomentMediaInput[] {
  return media
    .filter((m): m is PendingMedia & { storageKey: string } => Boolean(m.storageKey))
    .map((m) => ({ storageKey: m.storageKey, mime: m.mime, width: m.width, height: m.height }));
}

interface Store {
  put(item: OutboxItem): Promise<void>;
  get(clientId: string): Promise<OutboxItem | undefined>;
  all(): Promise<OutboxItem[]>;
  remove(clientId: string): Promise<void>;
  clear(): Promise<void>;
}

function memoryStore(): Store {
  const map = new Map<string, OutboxItem>();
  return {
    async put(item) {
      map.set(item.clientId, item);
    },
    async get(clientId) {
      return map.get(clientId);
    },
    async all() {
      return [...map.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    },
    async remove(clientId) {
      map.delete(clientId);
    },
    async clear() {
      map.clear();
    },
  };
}

function idbStore(factory: IDBFactory): Store {
  let opening: Promise<IDBDatabase> | null = null;

  function open(): Promise<IDBDatabase> {
    if (!opening) {
      opening = new Promise<IDBDatabase>((resolve, reject) => {
        const request = factory.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE)) {
            // keyPath is the idempotency key: one record per clientId, ever.
            db.createObjectStore(STORE, { keyPath: "clientId" });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("indexeddb open failed"));
      });
    }
    return opening;
  }

  async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) {
    const db = await open();
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("indexeddb request failed"));
    });
  }

  return {
    async put(item) {
      await tx("readwrite", (store) => store.put(item));
    },
    async get(clientId) {
      return (await tx("readonly", (store) => store.get(clientId))) as OutboxItem | undefined;
    },
    async all() {
      const rows = (await tx("readonly", (store) => store.getAll())) as OutboxItem[];
      return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    },
    async remove(clientId) {
      await tx("readwrite", (store) => store.delete(clientId));
    },
    async clear() {
      await tx("readwrite", (store) => store.clear());
    },
  };
}

let store: Store | null = null;

function getStore(): Store {
  if (!store) {
    const factory = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
    store = factory ? idbStore(factory) : memoryStore();
  }
  return store;
}

/** Test seam: force the in memory store and drop any queued state. */
export function resetOutboxForTests(): void {
  store = memoryStore();
}

const listeners = new Set<(count: number) => void>();

async function notify(): Promise<void> {
  if (listeners.size === 0) return;
  const count = (await getStore().all()).length;
  for (const listener of listeners) listener(count);
}

export function onOutboxChange(listener: (count: number) => void): () => void {
  listeners.add(listener);
  void notify();
  return () => {
    listeners.delete(listener);
  };
}

export interface EnqueueMomentArgs {
  clientId: string;
  tripId: string;
  payload: PendingMoment;
}

/**
 * Queue a moment. Calling this twice with the same clientId replaces the
 * single record, it never produces a second one.
 */
export async function enqueueMoment(args: EnqueueMomentArgs): Promise<OutboxMoment> {
  const existing = (await getStore().get(args.clientId)) as OutboxMoment | undefined;
  const item: OutboxMoment = {
    kind: "moment",
    clientId: args.clientId,
    tripId: args.tripId,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    attempts: existing?.attempts ?? 0,
    nextAttemptAt: 0,
    lastError: null,
    payload: args.payload,
  };
  await getStore().put(item);
  await notify();
  return item;
}

export async function enqueueTrack(
  clientId: string,
  tripId: string,
  points: TrackPointInput[],
): Promise<OutboxTrack> {
  const existing = (await getStore().get(clientId)) as OutboxTrack | undefined;
  const item: OutboxTrack = {
    kind: "track",
    clientId,
    tripId,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    attempts: existing?.attempts ?? 0,
    nextAttemptAt: 0,
    lastError: null,
    payload: { points },
  };
  await getStore().put(item);
  await notify();
  return item;
}

export async function listOutbox(): Promise<OutboxItem[]> {
  return getStore().all();
}

export async function outboxCount(): Promise<number> {
  return (await getStore().all()).length;
}

export async function saveOutboxItem(item: OutboxItem): Promise<void> {
  await getStore().put(item);
  await notify();
}

export async function removeOutboxItem(clientId: string): Promise<void> {
  await getStore().remove(clientId);
  await notify();
}

export async function clearOutbox(): Promise<void> {
  await getStore().clear();
  await notify();
}

/** Exponential backoff, capped at five minutes. */
export function backoffMs(attempts: number): number {
  return Math.min(5 * 60_000, 1000 * 2 ** Math.min(attempts, 10));
}
